import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';

import { MaterialSelection, MaterialSettingsPair } from '@paragon/contract-library';

import { RootState } from '../../../../../store';
import { closeSettingsPanel } from '../../../../../store/actions/appActions';
import { updateInsertingTrussFromSettings } from '../../../../../store/actions/insertComponentDesignActions';
import { saveMaterialSelection } from '../../../../../store/actions/inventorySetActions';
import {
  loadLumberPricesForMaterialSettings,
  loadMaterialSettingsWithSpinner,
} from '../../../../../store/actions/loadActions';
import { saveMaterialSettings } from '../../../../../store/actions/settingsActions';
import { getActiveFeaturePatternTrussSettings } from '../../../../../store/selectors/activeComponentFeaturePatternSettingsSelectors';
import { getProjectLayoutModel } from '../../../../../store/selectors/getProjectLayoutModel';
import {
  getSelectedModelForSettingsPanel,
  isInsertionComponentModel,
} from '../../../../../store/selectors/settingsPanelSelectors';
import {
  InsertingComponentDesignInfo,
  setInsertionMaterialSelection,
  setInsertionMaterialSettings,
} from '../../../../../store/slices/applicationSlice';
import { SaveOptions } from '../interfaces';
import { SaveAction } from '../SettingsSaveButton';
import { MaterialSettingsControls } from './MaterialSettingsControls';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  materialSettings: MaterialSettingsPair | null;
}

class MaterialSettingsPanel extends Component<Props, State> {
  state: State = {
    materialSettings: null,
  };

  async componentDidMount() {
    // We load the material settings when we start component insertion
    if (isInsertionComponentModel(this.props.selectedModel)) return;

    const materialSettings = await this.props.loadMaterialSettingsWithSpinner(this.props.selectedModel.model.guid);
    if (materialSettings) {
      this.setState({ materialSettings });
    } else {
      this.props.closeSettingsPanel();
    }
  }

  saveMaterialSettings = async (materialSettings: MaterialSettingsPair, saveAction: SaveAction) => {
    if (isInsertionComponentModel(this.props.selectedModel)) {
      this.props.setInsertionMaterialSettings({ settings: materialSettings, state: 'modified' });
      await this.props.loadLumberPricesForMaterialSettings(materialSettings);
      await this.props.updateInsertingTrussFromSettings({ preview: true });

      return;
    }

    const modelGuid = this.props.selectedModel.model.guid;

    let saveOptions: SaveOptions;
    if (saveAction === SaveAction.ActualForSingleComponent) {
      saveOptions = {
        modelGuid,
        action: saveAction,
      };
    } else {
      const { projectGuid } = this.props;
      if (projectGuid == null) throw new Error('Impossible attempt to save material settings without a project guid.');
      saveOptions = {
        modelGuid,
        projectGuid,
        action: saveAction,
      };
    }

    await this.props.saveMaterialSettings(materialSettings, saveOptions);
  };

  saveMaterialSelection = async (materialSelection: MaterialSelection, saveAction: SaveAction) => {
    const { selectedModel } = this.props;

    if (isInsertionComponentModel(selectedModel)) {
      this.props.setInsertionMaterialSelection(materialSelection);
    } else {
      await this.props.saveMaterialSelection(selectedModel.model.guid, materialSelection, saveAction);
    }
  };

  render() {
    const { selectedModel } = this.props;
    const isInsertionComponent = isInsertionComponentModel(selectedModel);

    const featurePatternTrussSettings = isInsertionComponent
      ? (this.props.featurePatternTrussSettings ?? undefined)
      : undefined;

    const materialSettings = isInsertionComponent
      ? this.props.insertionInfo!.materialSettings
      : this.state.materialSettings;

    const materialSelection = isInsertionComponent
      ? this.props.insertionInfo!.materialSelection
      : selectedModel.model.materialSelection;

    if (materialSettings == null || materialSelection == null) return null;

    return (
      <MaterialSettingsControls
        selectedModel={selectedModel}
        featurePatternTrussSettings={featurePatternTrussSettings}
        materialSettings={materialSettings}
        materialSelection={materialSelection}
        saveMaterialSettings={this.saveMaterialSettings}
        saveMaterialSelection={this.saveMaterialSelection}
      />
    );
  }
}

function mapStateToProps(state: RootState) {
  const project = getProjectLayoutModel(state);
  const { insertingObjectInfo } = state.application;
  const selectedModel = getSelectedModelForSettingsPanel(state);

  if (selectedModel == null) {
    throw new Error('The MaterialSettingsPanel should never be rendered when a model is not selected.');
  }

  const result = {
    selectedModel,
    projectGuid: project?.guid,
    featurePatternTrussSettings: getActiveFeaturePatternTrussSettings(state),
    insertionInfo: null as InsertingComponentDesignInfo | null,
  };

  if (isInsertionComponentModel(selectedModel)) {
    if (
      insertingObjectInfo?.type === 'componentDesign' &&
      insertingObjectInfo.loadedInfo.isLoaded &&
      insertingObjectInfo.loadedInfo.guid === selectedModel.model.guid
    ) {
      result.insertionInfo = insertingObjectInfo;
    } else {
      throw new Error('Invalid state—editing material settings for an insertion component that is not loaded.');
    }
  }

  return result;
}

const actionCreators = {
  closeSettingsPanel,
  loadMaterialSettingsWithSpinner,
  saveMaterialSelection,
  saveMaterialSettings,
  setInsertionMaterialSettings,
  updateInsertingTrussFromSettings,
  setInsertionMaterialSelection,
  loadLumberPricesForMaterialSettings,
};

export default connect(mapStateToProps, actionCreators)(MaterialSettingsPanel);
